import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useApp, useUi } from '../app/context';
import { buildCommands, filterCommands, GROUP_ORDER, type Command } from '../app/commands';
import { patchUi } from '../app/ui';
import { CheckIcon, SearchIcon } from './icons';
import { shortcutHint } from './keys';
import { usePresence } from './Popover';

/**
 * ⌘K: a small, fast list of what MyTXT can do right now, and every open
 * document. Not a command line — a short menu you can type into.
 */
export function Palette() {
  const open = useUi((s) => s.palette);
  const { mounted, closing } = usePresence(open, 140);
  // Where focus was when the palette opened: it goes back there afterwards.
  const [returnTo, setReturnTo] = useState<HTMLElement | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReturnTo(document.activeElement as HTMLElement | null);
  }
  if (!mounted) return null;
  return <PaletteSurface closing={closing} returnTo={returnTo} />;
}

function PaletteSurface({ closing, returnTo }: { closing: boolean; returnTo: HTMLElement | null }) {
  const app = useApp();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const commands = useMemo(() => buildCommands(app), [app]);
  const results = useMemo(() => filterCommands(commands, query), [commands, query]);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lamp = useRef<HTMLDivElement>(null);
  const grouped = !query.trim();
  const selected = results[Math.min(index, results.length - 1)];

  // Without a query, results are shown grouped; keep the selection order identical to the display order.
  const ordered = useMemo(() => (grouped ? GROUP_ORDER.flatMap((g) => results.filter((c) => c.group === g)) : results), [grouped, results]);
  const active = ordered[Math.min(index, ordered.length - 1)] ?? selected;

  useEffect(() => {
    if (!closing) input.current?.focus({ preventScroll: true });
  }, [closing]);

  // The selection is one soft plate that glides from row to row.
  useLayoutEffect(() => {
    const listEl = listRef.current;
    const plate = lamp.current;
    const row = listEl?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!plate || !row || !listEl) {
      if (plate) plate.style.opacity = '0';
      return;
    }
    plate.style.opacity = '1';
    plate.style.transform = `translateY(${row.offsetTop}px)`;
    plate.style.height = `${row.offsetHeight}px`;
    const top = row.offsetTop - 6;
    const bottom = row.offsetTop + row.offsetHeight + 6;
    if (top < listEl.scrollTop) listEl.scrollTop = top;
    else if (bottom > listEl.scrollTop + listEl.clientHeight) listEl.scrollTop = bottom - listEl.clientHeight;
    if (!plate.dataset.ready) requestAnimationFrame(() => (plate.dataset.ready = 'true'));
  }, [active, ordered]);

  const close = (restore = true) => {
    patchUi({ palette: false });
    if (restore) requestAnimationFrame(() => returnTo?.isConnected && returnTo.focus({ preventScroll: true }));
  };

  const run = (command: Command | undefined) => {
    if (!command) return;
    close(false);
    // Let focus return to the work first, so the command acts where the person was.
    requestAnimationFrame(() => {
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
      command.run();
    });
  };

  const keyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault();
      setIndex((i) => (i + 1) % Math.max(1, ordered.length));
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault();
      setIndex((i) => (i - 1 + ordered.length) % Math.max(1, ordered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  return (
    <div className="palette-layer" data-state={closing ? 'closing' : 'open'} onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Commands" onKeyDown={keyDown}>
        <div className="palette-field">
          <SearchIcon size={17} />
          <input
            ref={input}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={active ? `cmd-${active.id}` : undefined}
            aria-label="Search commands and documents"
            placeholder="Type a command or a document name"
            value={query}
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
          />
          <kbd className="palette-esc">esc</kbd>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" aria-label="Commands" ref={listRef}>
          <div className="palette-plate" ref={lamp} aria-hidden="true" />
          {ordered.length === 0 && <p className="palette-empty">Nothing matches “{query}”.</p>}
          {ordered.map((c, i) => {
            const heading = grouped && c.group !== ordered[i - 1]?.group ? c.group : null;
            const hint = c.shortcut ? shortcutHint(c.shortcut) : undefined;
            return (
              <div key={c.id} className="palette-row-wrap">
                {heading && (
                  <div className="palette-group" role="presentation">
                    {heading}
                  </div>
                )}
                <div
                  id={`cmd-${c.id}`}
                  role="option"
                  aria-selected={c === active}
                  className="palette-row"
                  onPointerMove={() => i !== index && setIndex(i)}
                  onClick={() => run(c)}
                >
                  <span className="palette-label">{c.label}</span>
                  {c.detail && <span className="palette-detail">{c.detail}</span>}
                  {c.checked && (
                    <span className="palette-check" aria-label="on">
                      <CheckIcon size={14} />
                    </span>
                  )}
                  {hint && <kbd className="palette-kbd">{hint}</kbd>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
